import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Play, Clock, Award, Shield, CheckCircle, Upload, Settings, Download, Star, Mail, Phone, MapPin, Send, ArrowRight, X } from 'lucide-react';
import { services, companyInfo, testimonials, editingProcessSteps, serviceDescriptions, heroCarouselImages } from '../mock';
import { videoFileIds } from '../videoData';
import AnimatedCounter from '../components/AnimatedCounter';
import DraggableBeforeAfter from '../components/DraggableBeforeAfter';
import RevealSection from '../components/RevealSection';

/* ================================================================
   HERO SECTION
   ================================================================ */
const HeroSection = () => {
  const imgs = heroCarouselImages;
  const n = imgs.length;
  const [idx, setIdx] = useState(0);
  const [grabbing, setGrabbing] = useState(false);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const pos = useRef(1);
  const drag = useRef(null);
  const dragMoved = useRef(false);
  const rafId = useRef(0);
  const timer = useRef(null);
  const snapTimer = useRef(null);
  const reduced = useRef(false);

  const slideW = () => containerRef.current?.clientWidth || 1;

  const setTrack = useCallback((offsetPx = 0, animate = false) => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transition = animate
      ? (reduced.current ? 'transform 250ms ease-out' : 'transform 650ms cubic-bezier(0.25, 0.1, 0.25, 1)')
      : 'none';
    track.style.transform = `translate3d(${-pos.current * slideW() + offsetPx}px, 0, 0)`;
  }, []);

  const stopAuto = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
  }, []);

  const scheduleWrap = useCallback(() => {
    clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => {
      if (pos.current <= 0) { pos.current = n; setTrack(0, false); }
      else if (pos.current >= n + 1) { pos.current = 1; setTrack(0, false); }
    }, reduced.current ? 300 : 700);
  }, [n, setTrack]);

  const startAuto = useCallback(() => {
    stopAuto();
    timer.current = setInterval(() => {
      pos.current += 1;
      setIdx(((pos.current - 1) % n + n) % n);
      setTrack(0, true);
      scheduleWrap();
    }, 6000);
  }, [n, setTrack, stopAuto, scheduleWrap]);

  useLayoutEffect(() => {
    reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTrack(0, false);
  }, [setTrack]);

  useEffect(() => {
    startAuto();
    const onResize = () => setTrack(0, false);
    const onVis = () => (document.hidden ? stopAuto() : startAuto());
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopAuto();
      clearTimeout(snapTimer.current);
      cancelAnimationFrame(rafId.current);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [startAuto, stopAuto, setTrack]);

  useEffect(() => {
    imgs.forEach((img) => {
      const im = new Image();
      im.referrerPolicy = 'no-referrer';
      im.src = img.after;
    });
  }, [imgs]);

  useEffect(() => {
    if (!grabbing) return;
    const onMove = (e) => {
      const d = drag.current;
      if (!d?.active) return;
      d.dx = e.clientX - d.x;
      if (Math.abs(d.dx) > 6) dragMoved.current = true;
      if (!rafId.current) {
        rafId.current = requestAnimationFrame(() => {
          rafId.current = 0;
          if (drag.current?.active) setTrack(drag.current.dx, false);
        });
      }
    };
    const onEnd = (e) => {
      const d = drag.current;
      if (!d?.active) return;
      d.active = false;
      setGrabbing(false);
      if (trackRef.current) trackRef.current.style.willChange = 'auto';
      const w = slideW();
      const dx = e.type === 'pointercancel' ? 0 : d.dx || 0;
      const dt = performance.now() - d.t;
      const velocity = dt > 0 ? Math.abs(dx) / dt : 0;
      const base = pos.current;
      let target;
      if (e.type !== 'pointercancel' && (Math.abs(dx) > w * 0.18 || velocity > 0.35)) {
        target = Math.round(base) + (dx < 0 ? 1 : -1);
      } else {
        target = Math.round(base);
      }
      pos.current = target;
      setIdx(((target - 1) % n + n) % n);
      setTrack(0, true);
      scheduleWrap();
      startAuto();
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
    };
  }, [grabbing, n, setTrack, scheduleWrap, startAuto]);

  const onPointerDown = (e) => {
    stopAuto();
    clearTimeout(snapTimer.current);
    cancelAnimationFrame(rafId.current);
    rafId.current = 0;
    const track = trackRef.current;
    const w = slideW();
    if (track && w > 1) {
      try {
        const m = new DOMMatrixReadOnly(getComputedStyle(track).transform);
        if (Number.isFinite(m.m41)) pos.current = -m.m41 / w;
      } catch {}
    }
    drag.current = { x: e.clientX, dx: 0, t: performance.now(), active: true };
    dragMoved.current = false;
    setGrabbing(true);
    if (trackRef.current) trackRef.current.style.willChange = 'transform';
    setTrack(0, false);
  };

  const onClickCapture = (e) => {
    if (dragMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      dragMoved.current = false;
    }
  };

  const goToSlide = (i) => {
    stopAuto();
    pos.current = i + 1;
    setIdx(i);
    setTrack(0, true);
    startAuto();
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="hero"
      ref={containerRef}
      className={`relative h-screen min-h-[600px] overflow-hidden select-none ${grabbing ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{ touchAction: 'pan-y' }}
      data-testid="hero-section"
      onPointerDown={onPointerDown}
      onClickCapture={onClickCapture}
    >
      {/* Slide Track (clone-last + slides + clone-first for infinite loop) */}
      <div ref={trackRef} className="flex h-full">
        {[imgs[n - 1], ...imgs, imgs[0]].map((img, i) => (
          <div key={i} className="w-full h-full flex-shrink-0">
            <img
              src={img.after}
              alt={img.title}
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
              draggable={false}
              loading="eager"
              decoding="async"
            />
          </div>
        ))}
      </div>

      {/* Gradient Overlay — diagonal: strongest bottom-left, near-clear top-right */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{
          background: 'linear-gradient(to top right, rgba(10,11,14,0.92) 0%, rgba(10,11,14,0.78) 22%, rgba(10,11,14,0.55) 45%, rgba(10,11,14,0.3) 65%, rgba(10,11,14,0.12) 82%, rgba(10,11,14,0.02) 100%)'
        }}
      />

      {/* Content */}
      <div className="absolute inset-0 z-[3] h-full flex flex-col justify-end pb-16 md:pb-24 px-5 md:px-8 pointer-events-none">
        <div className="max-w-7xl mx-auto w-full pointer-events-auto">
          {/* Overline */}
          <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-4 animate-fade-in-up">
            Professional Real Estate Editing
          </p>

          {/* Heading */}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F8FAFC] leading-[1.1] mb-6 max-w-3xl animate-fade-in-up delay-100">
            {companyInfo.tagline.split('.').map((part, i) => (
              <span key={i}>
                {part.trim()}
                {i === 0 && <span className="text-[#D4AF37]">.</span>}
                {i === 0 && <br />}
                {i === 1 && <span className="text-[#D4AF37]">.</span>}
              </span>
            ))}
          </h1>

          {/* Service Label */}
          <p className="text-xs uppercase tracking-[0.2em] font-sans text-[#94A3B8] mb-8 animate-fade-in-up delay-200">
            {imgs[idx].service}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in-up delay-300">
            <button
              onClick={() => scrollTo('featured-work')}
              className="px-8 py-3.5 bg-[#D4AF37] text-[#0A0B0E] rounded-full font-sans font-semibold text-sm tracking-wide btn-premium"
              data-testid="hero-explore-btn"
            >
              Explore Our Work
            </button>
            <button
              onClick={() => scrollTo('contact')}
              className="px-8 py-3.5 border border-[#F8FAFC]/20 text-[#F8FAFC] rounded-full font-sans font-semibold text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
              data-testid="hero-contact-btn"
            >
              Request Inquiry
            </button>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap gap-8 md:gap-12 animate-fade-in-up delay-400">
            {[
              { end: 10, suffix: 'K+', label: 'Images Edited', delay: 0 },
              { end: 500, suffix: '+', label: 'Happy Clients', delay: 150 },
              { end: 15, suffix: '+', label: 'Countries', delay: 300 },
              { end: 24, suffix: 'hr', label: 'Turnaround', delay: 450 },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-serif text-2xl md:text-3xl font-semibold text-[#D4AF37]">
                  <AnimatedCounter end={s.end} suffix={s.suffix} delay={s.delay} />
                </div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#64748B] font-sans mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 right-5 md:right-8 flex gap-2 z-10 pointer-events-auto">
          {imgs.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`rounded-full transition-all duration-400 ${
                i === idx ? 'bg-[#D4AF37] w-8 h-2' : 'bg-white/20 w-2 h-2 hover:bg-white/40'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   FULL-SCREEN LIGHTBOX (Before/After with Swipe Between Pairs)
   ================================================================ */
const FullScreenLightbox = ({ images, initialIndex, onClose }) => {
  const [idx, setIdx] = useState(initialIndex);
  const n = images.length;
  const dragRef = useRef(null);
  const dragMoved = useRef(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx(prev => (prev + 1) % n);
      if (e.key === 'ArrowLeft') setIdx(prev => (prev - 1 + n) % n);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [n, onClose]);

  const onPointerDown = (e) => {
    if (e.target.closest('[data-testid="draggable-comparison-container"]')) return;
    dragRef.current = { x: e.clientX, t: performance.now() };
    dragMoved.current = false;
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    if (Math.abs(e.clientX - dragRef.current.x) > 10) dragMoved.current = true;
  };

  const onPointerUp = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dt = performance.now() - dragRef.current.t;
    const velocity = dt > 0 ? Math.abs(dx) / dt : 0;
    dragRef.current = null;
    if (Math.abs(dx) > 80 || velocity > 0.4) {
      if (dx < 0) setIdx(prev => (prev + 1) % n);
      else setIdx(prev => (prev - 1 + n) % n);
    }
  };

  const onClickCapture = (e) => {
    if (dragMoved.current) { e.preventDefault(); e.stopPropagation(); dragMoved.current = false; }
  };

  const pair = images[idx];
  if (!pair) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0B0E]/95 lightbox-dark flex flex-col"
      data-testid="featured-lightbox"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClickCapture={onClickCapture}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 md:px-8 py-4 shrink-0 relative z-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-sans">{pair.service}</p>
          <p className="text-sm text-[#94A3B8] font-sans mt-0.5">{pair.title}</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-[#64748B] font-sans tabular-nums">{idx + 1} / {n}</span>
          <button
            onClick={onClose}
            data-testid="lightbox-close-btn"
            className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-white/30 transition-all"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 md:px-16 pb-6 min-h-0 relative">
        {/* Left Arrow */}
        <button
          onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + n) % n); }}
          data-testid="lightbox-prev-btn"
          className="absolute left-2 md:left-4 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#0A0B0E]/70 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-[#D4AF37]/50 transition-all"
          aria-label="Previous"
        >
          <ChevronLeft size={20} />
        </button>

        {/* Before/After Slider */}
        <div className="w-full max-w-5xl mx-auto h-full flex items-center">
          <div className="w-full" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            <DraggableBeforeAfter
              key={idx}
              beforeImage={pair.before}
              afterImage={pair.after}
              title={pair.title}
            />
          </div>
        </div>

        {/* Right Arrow */}
        <button
          onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % n); }}
          data-testid="lightbox-next-btn"
          className="absolute right-2 md:right-4 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#0A0B0E]/70 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:border-[#D4AF37]/50 transition-all"
          aria-label="Next"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

/* ================================================================
   FEATURED WORK GRID
   ================================================================ */
const FeaturedWorkSection = () => {
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const categories = useMemo(() => {
    const cats = [{ id: 'all', name: 'All Work' }];
    services.forEach(s => {
      if (s.images && s.images.length > 0) {
        cats.push({ id: s.id, name: s.title });
      }
    });
    return cats;
  }, []);

  const displayImages = useMemo(() => {
    if (filter === 'all') {
      const picks = [];
      services.forEach(s => {
        if (s.images && s.images.length > 0) {
          picks.push({ ...s.images[0], service: s.title, serviceId: s.id });
          if (s.images.length > 1) picks.push({ ...s.images[1], service: s.title, serviceId: s.id });
        }
      });
      return picks.slice(0, 12);
    }
    const svc = services.find(s => s.id === filter);
    return svc ? svc.images.slice(0, 8).map(img => ({ ...img, service: svc.title, serviceId: svc.id })) : [];
  }, [filter]);

  return (
    <section id="featured-work" className="py-20 md:py-28 px-5 md:px-8" data-testid="featured-work-grid">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">Portfolio</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#F8FAFC] mb-4">Featured Work</h2>
          <div className="section-divider mb-10" />
        </RevealSection>

        {/* Filters */}
        <RevealSection delay={100}>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="featured-filters">
            {categories.map(c => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                data-testid={`featured-filter-${c.id}`}
                className={`px-4 py-2 rounded-full text-xs font-sans font-medium tracking-wide transition-all duration-300 ${
                  filter === c.id
                    ? 'bg-[#D4AF37] text-[#0A0B0E]'
                    : 'border border-themed text-[#94A3B8] hover:border-[#D4AF37] hover:text-[#D4AF37]'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </RevealSection>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayImages.map((img, i) => (
            <RevealSection key={`${img.service}-${i}`} delay={i * 50}>
              <div
                className="group relative overflow-hidden rounded-lg cursor-pointer card-hover border border-themed"
                onClick={() => setLightbox(img)}
                data-testid={`featured-item-${i}`}
              >
                <div className="aspect-[4/3] bg-[#12141A]">
                  <img
                    src={img.after}
                    alt={img.title || img.service}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-sans">{img.service}</p>
                  <p className="text-sm text-[#F8FAFC] font-sans mt-1">{img.title}</p>
                </div>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>

      {/* Full-Screen Lightbox with Swipe Navigation */}
      {lightbox !== null && (
        <FullScreenLightbox
          images={displayImages.filter(img => img.before && img.after)}
          initialIndex={(() => {
            const filtered = displayImages.filter(img => img.before && img.after);
            return Math.max(0, filtered.findIndex(img => img === lightbox));
          })()}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
};

/* ================================================================
   BEFORE / AFTER INTERACTIVE SLIDERS
   ================================================================ */
const BeforeAfterSection = () => {
  const pairs = useMemo(() => {
    const picks = [];
    services.forEach(s => {
      if (s.images && s.images.length > 0) {
        picks.push({ ...s.images[0], service: s.title });
      }
    });
    return picks.filter(p => p.before && p.after).slice(0, 6);
  }, []);

  return (
    <section id="before-after" className="py-20 md:py-28 px-5 md:px-8 bg-[#12141A]" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">Comparison</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#F8FAFC] mb-4">Before & After</h2>
          <p className="text-sm text-[#94A3B8] font-sans max-w-2xl mb-4">Drag the slider to reveal the transformation. Every image is manually edited for natural, realistic results.</p>
          <div className="section-divider mb-12" />
        </RevealSection>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pairs.map((pair, i) => (
            <RevealSection key={i} delay={i * 80}>
              <div data-testid={`draggable-comparison-container-${i}`}>
                <DraggableBeforeAfter beforeImage={pair.before} afterImage={pair.after} title={pair.title} />
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-sans mt-3">{pair.service}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   VIDEO SHOWCASE
   ================================================================ */
const VideoShowcaseSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section id="video-showcase" className="py-20 md:py-28 px-5 md:px-8" data-testid="video-showcase-section">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">Cinematic</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#F8FAFC] mb-4">Video Showcase</h2>
          <p className="text-sm text-[#94A3B8] font-sans max-w-2xl mb-4">Professional video editing for property tours, drone footage, and cinematic walkthroughs.</p>
          <div className="section-divider mb-12" />
        </RevealSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {videoFileIds.map((video, i) => (
            <RevealSection key={i} delay={i * 80}>
              <div
                className="relative rounded-lg overflow-hidden border border-themed card-hover bg-[#12141A]"
                data-testid={`video-item-${i}`}
              >
                {activeVideo === i ? (
                  <iframe
                    src={`https://drive.google.com/file/d/${video.id}/preview`}
                    className="w-full aspect-video"
                    allow="autoplay"
                    allowFullScreen
                    title={video.title}
                  />
                ) : (
                  <div
                    className="relative w-full aspect-video bg-[#161922] cursor-pointer group"
                    onClick={() => setActiveVideo(i)}
                    data-testid={`video-modal-trigger-${i}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-lg shadow-[#D4AF37]/20">
                        <Play className="text-[#0A0B0E] ml-1" size={24} fill="#0A0B0E" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <p className="text-xs font-sans font-medium text-[#94A3B8]">{video.title}</p>
                    </div>
                  </div>
                )}
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   SERVICES SECTION
   ================================================================ */
const ServicesSection = () => {
  const iconMap = {
    Camera: '📷', Image: '🖼️', Zap: '⚡', Sunset: '🌅',
    Home: '🏠', Trash2: '🗑️', Sofa: '🛋️', Video: '🎬',
  };

  return (
    <section id="services" className="py-20 md:py-28 px-5 md:px-8 bg-[#12141A]" data-testid="services-grid">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">What We Do</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#F8FAFC] mb-4">Our Services</h2>
          <p className="text-sm text-[#94A3B8] font-sans max-w-2xl mb-4">Comprehensive editing solutions for every real estate need</p>
          <div className="section-divider mb-12" />
        </RevealSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((svc, i) => (
            <RevealSection key={svc.id} delay={i * 60}>
              <a
                href={`/services/${svc.id}`}
                className="block group p-6 md:p-8 rounded-xl border border-themed bg-[#161922] card-hover"
                data-testid={`service-card-${svc.id}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-5 transition-all duration-300 group-hover:bg-[#D4AF37]/20 group-hover:border-[#D4AF37]/40">
                  <span className="text-[#D4AF37] font-serif text-lg font-semibold">{svc.title.charAt(0)}</span>
                </div>
                <h3 className="font-serif text-xl text-[#F8FAFC] mb-2 group-hover:text-[#D4AF37] transition-colors duration-300">{svc.title}</h3>
                <p className="text-sm text-[#94A3B8] font-sans leading-relaxed mb-4 line-clamp-2">{svc.shortDesc}</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-sans font-medium text-[#D4AF37] tracking-wide uppercase">
                  Learn More <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </a>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   ABOUT SECTION
   ================================================================ */
const AboutSection = () => (
  <section id="about" className="py-20 md:py-28 px-5 md:px-8" data-testid="about-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <RevealSection>
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">About Us</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#F8FAFC] mb-4">PEC STUDIOS</h2>
            <div className="section-divider mb-8" />
            <div className="space-y-4 text-sm text-[#94A3B8] font-sans leading-relaxed">
              <p>PEC STUDIOS was founded with a simple mission: to provide real estate professionals with editing services that truly make their properties stand out.</p>
              <p>We understand that in real estate, first impressions are everything. That's why we combine technical expertise with an artistic eye to create images that not only look professional but also evoke emotion and help buyers envision themselves in the space.</p>
              <p>Our team of experienced editors has worked with photographers, agencies, and brokers from around the world, delivering consistent quality that our clients can rely on.</p>
            </div>
          </div>
        </RevealSection>

        <RevealSection delay={150}>
          <div className="grid grid-cols-2 gap-4">
            {[
              { end: 10, suffix: 'K+', label: 'Images Edited', desc: 'Professional results' },
              { end: 500, suffix: '+', label: 'Happy Clients', desc: 'Worldwide trust' },
              { end: 15, suffix: '+', label: 'Countries', desc: 'Global reach' },
              { end: 24, suffix: 'hr', label: 'Turnaround', desc: 'Fast delivery' },
            ].map((s, i) => (
              <div key={i} className="p-5 md:p-6 rounded-xl border border-themed bg-[#12141A] text-center">
                <div className="font-serif text-2xl md:text-3xl font-semibold text-[#D4AF37] mb-1">
                  <AnimatedCounter end={s.end} suffix={s.suffix} delay={i * 150} />
                </div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#F8FAFC] font-sans font-medium">{s.label}</div>
                <div className="text-[10px] text-[#64748B] font-sans mt-1">{s.desc}</div>
              </div>
            ))}
          </div>
        </RevealSection>
      </div>

      {/* Testimonials */}
      <div className="mt-20">
        <RevealSection>
          <h3 className="font-serif text-2xl text-[#F8FAFC] mb-8 text-center">What Clients Say</h3>
        </RevealSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <RevealSection key={i} delay={i * 80}>
              <div className="p-6 rounded-xl border border-themed bg-[#12141A]">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-sm text-[#94A3B8] font-sans leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="border-t border-themed pt-4">
                  <p className="text-sm font-sans font-medium text-[#F8FAFC]">{t.name}</p>
                  <p className="text-xs text-[#64748B] font-sans">{t.role}</p>
                </div>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ================================================================
   PROCESS + FAQ SECTION
   ================================================================ */
const ProcessFAQSection = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    { q: "What's your turnaround time?", a: "Standard turnaround is 24 hours. Rush orders can be delivered in 6-12 hours for an additional fee." },
    { q: "What file formats do you accept?", a: "We accept JPG, PNG, TIFF, and RAW files (CR2, NEF, ARW, etc.) from all major camera brands." },
    { q: "Do you offer bulk discounts?", a: "Yes! We offer volume discounts for high-volume clients. Contact us to discuss your needs." },
    { q: "Can I request revisions?", a: "Absolutely! We offer free revisions to ensure you're completely satisfied with the results." },
    { q: "How do I send you my photos?", a: "You can upload via Dropbox, Google Drive, WeTransfer, or any file sharing service. We'll provide details after your inquiry." },
    { q: "Is my data secure?", a: "Yes! All files are transferred securely and deleted after 30 days. We maintain strict confidentiality." },
  ];

  const steps = [
    { Icon: Upload, title: 'Upload', desc: 'Send us images via Google Drive, Dropbox, or any link', num: 1 },
    { Icon: Settings, title: 'We Edit', desc: 'We edit images by applying our special editing process', num: 2 },
    { Icon: CheckCircle, title: 'Review', desc: 'Quality checked for perfection', num: 3 },
    { Icon: Download, title: 'Deliver', desc: 'We send edited files via Google Drive or Dropbox', num: 4 },
  ];

  return (
    <section id="process-faq" className="py-20 md:py-28 px-5 md:px-8 bg-[#12141A]" data-testid="process-faq-section">
      <div className="max-w-7xl mx-auto">
        {/* Process */}
        <RevealSection>
          <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">Workflow</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#F8FAFC] mb-4">How We Work</h2>
          <p className="text-sm text-[#94A3B8] font-sans mb-4">Simple process, stunning results</p>
          <div className="section-divider mb-12" />
        </RevealSection>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8 mb-20">
          {steps.map((step, i) => (
            <RevealSection key={step.num} delay={i * 100}>
              <div className="text-center group">
                <div className="relative mb-5">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mx-auto transition-all duration-500 group-hover:bg-[#D4AF37]/20 group-hover:border-[#D4AF37]/40 group-hover:scale-105">
                    <step.Icon className="text-[#D4AF37]" size={24} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center text-[#0A0B0E] font-sans font-bold text-xs shadow-lg">
                    {step.num}
                  </div>
                </div>
                <h3 className="font-serif text-lg text-[#F8FAFC] mb-1">{step.title}</h3>
                <p className="text-xs text-[#94A3B8] font-sans">{step.desc}</p>
              </div>
            </RevealSection>
          ))}
        </div>

        {/* FAQ */}
        <RevealSection>
          <h3 className="font-serif text-2xl sm:text-3xl text-[#F8FAFC] mb-8 text-center">Frequently Asked Questions</h3>
        </RevealSection>

        <div className="max-w-3xl mx-auto space-y-3" data-testid="faq-accordion">
          {faqs.map((faq, i) => (
            <RevealSection key={i} delay={i * 50}>
              <div className="border border-themed rounded-xl overflow-hidden bg-[#161922]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`faq-item-${i}`}
                >
                  <span className="text-sm font-sans font-medium text-[#F8FAFC] pr-4">{faq.q}</span>
                  <span className={`text-[#D4AF37] transition-transform duration-300 flex-shrink-0 ${openFaq === i ? 'rotate-45' : ''}`}>
                    <span className="text-xl leading-none">+</span>
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40 pb-5' : 'max-h-0'}`}>
                  <p className="px-5 text-sm text-[#94A3B8] font-sans leading-relaxed">{faq.a}</p>
                </div>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   CONTACT SECTION
   ================================================================ */
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', country: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', country: '', service: '', message: '' });
        setTimeout(() => setSubmitted(false), 6000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to send message. Please check your connection and try again.');
    }
    setIsSubmitting(false);
  };

  const inputClass = "w-full bg-[#0A0B0E] border border-themed text-[#F8FAFC] px-4 py-3 rounded-lg font-sans text-sm focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 placeholder:text-[#64748B]";

  return (
    <section id="contact" className="py-20 md:py-28 px-5 md:px-8" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info */}
          <RevealSection>
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">Contact</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#F8FAFC] mb-4">Get In Touch</h2>
              <div className="section-divider mb-8" />
              <p className="text-sm text-[#94A3B8] font-sans leading-relaxed mb-10">
                Let's make your listings stand out. Send us a message and we'll respond within 24 hours.
              </p>

              <div className="space-y-5 mb-10">
                {[
                  { Icon: Mail, label: 'Email', value: companyInfo.email, href: `mailto:${companyInfo.email}` },
                  { Icon: Phone, label: 'Phone', value: companyInfo.phone, href: `tel:${companyInfo.phone}` },
                  { Icon: MapPin, label: 'Location', value: companyInfo.location },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <item.Icon className="text-[#D4AF37]" size={16} />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-sans mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-sm text-[#94A3B8] hover:text-[#D4AF37] transition-colors duration-300 font-sans">{item.value}</a>
                      ) : (
                        <p className="text-sm text-[#94A3B8] font-sans">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-5 rounded-xl border border-themed bg-[#12141A]">
                <p className="text-xs font-sans font-semibold text-[#D4AF37] uppercase tracking-wide mb-2">Business Hours</p>
                <p className="text-sm text-[#94A3B8] font-sans">Monday - Sunday: 24 Hours</p>
                <p className="text-xs text-[#D4AF37] font-sans mt-2">Available 24/7, every day of the week</p>
              </div>
            </div>
          </RevealSection>

          {/* Form */}
          <RevealSection delay={150}>
            <form onSubmit={handleSubmit} className="p-6 md:p-8 rounded-xl border border-themed bg-[#12141A]" data-testid="contact-form">
              {submitted && (
                <div className="mb-6 p-4 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-sm text-[#D4AF37] font-sans" data-testid="contact-success-message">
                  Message sent! We'll get back to you within 24 hours.
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400 font-sans" data-testid="contact-error-message">
                  {error}
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#64748B] font-sans font-medium mb-2">Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClass} placeholder="Your name" data-testid="contact-input-name" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#64748B] font-sans font-medium mb-2">Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} placeholder="your.email@example.com" data-testid="contact-input-email" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#64748B] font-sans font-medium mb-2">Country</label>
                  <input type="text" name="country" value={formData.country} onChange={handleChange} className={inputClass} placeholder="Your country" data-testid="contact-input-country" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#64748B] font-sans font-medium mb-2">Service Required *</label>
                  <select name="service" value={formData.service} onChange={handleChange} required className={inputClass} data-testid="contact-input-service">
                    <option value="">Select a service</option>
                    <option value="hdr">HDR Image Editing</option>
                    <option value="single">Single Image Editing</option>
                    <option value="flambient">Flash / Flambient Editing</option>
                    <option value="twilight">Day to Dusk / Twilight</option>
                    <option value="removal">Item Removal</option>
                    <option value="staging">Virtual Staging</option>
                    <option value="video">Real Estate Video Editing</option>
                    <option value="other">Other / Multiple Services</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#64748B] font-sans font-medium mb-2">Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={`${inputClass} resize-none`} placeholder="Tell us about your project..." data-testid="contact-input-message" />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-3.5 bg-[#D4AF37] text-[#0A0B0E] rounded-full font-sans font-semibold text-sm tracking-wide btn-premium flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? 'Sending...' : (<><Send size={16} /> Send Message</>)}
                </button>
              </div>
            </form>
          </RevealSection>
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   HOME PAGE (Single-Page Portfolio)
   ================================================================ */
const Home = () => {
  return (
    <div className="bg-[#0A0B0E] min-h-screen">
      <HeroSection />
      <FeaturedWorkSection />
      <BeforeAfterSection />
      <VideoShowcaseSection />
      <ServicesSection />
      <AboutSection />
      <ProcessFAQSection />
      <ContactSection />
    </div>
  );
};

export default Home;
